import {
  ClinicianAssistantContext,
  ClinicianAssistantLink,
} from "../api/contracts";
import {
  clientHomeworkDetailPath,
  clientPrimaryNavigation,
  clientProfilePath,
  insightsNavigation,
  teamOverviewPath,
  teamClientDetailPath,
  teamHomeworkPath,
  teamHomeworkDetailPath,
  teamReportsPath,
} from "../config/navigation";
import { ClinicianPortalRoute } from "./portalRoutes";

const reportPaths: Record<string, string> = {
  "caseload-engagement": "/clinician-portal/caseload-engagement",
  "caseload-insights": "/clinician-portal/caseload-engagement",
  "homework-overview": "/clinician-portal/homework-overview",
  "completion-by-device": "/clinician-portal/completion-by-device",
  "value-engagement": "/clinician-portal/value-engagement",
};

const teamReportKeys: Record<string, string> = {
  "caseload-engagement": "caseload",
  "caseload-insights": "caseload",
  "homework-overview": "homework",
  "completion-by-device": "device",
  "value-engagement": "value",
};

function patientIdFromRoute(route: ClinicianPortalRoute): string | undefined {
  return "patientId" in route ? route.patientId : undefined;
}

function homeworkAssignmentIdFromRoute(
  route: ClinicianPortalRoute,
): string | undefined {
  return "homeworkAssignmentId" in route
    ? route.homeworkAssignmentId
    : undefined;
}

export function clinicianAssistantContextForRoute(
  route: ClinicianPortalRoute,
  patientNickname?: string,
): ClinicianAssistantContext {
  const patientId = patientIdFromRoute(route);
  const homeworkAssignmentId = homeworkAssignmentIdFromRoute(route);
  let page: ClinicianAssistantContext["page"] = "other";

  switch (route.kind) {
    case "home":
      page = "home";
      break;
    case "clients":
    case "client-new":
      page = "clients";
      break;
    case "team":
    case "team-clients":
      page = "supervisor-team";
      break;
    case "client-current-homework":
    case "client-homework-history":
      page = "client-homework";
      break;
    case "client-clinical-trends":
    case "client-usage-engagement":
    case "client-practice-calendar":
    case "client-progress-reports":
      page = "client-insights";
      break;
    case "client-homework-detail":
    case "homework-activity-detail":
    case "homework-activity-steps":
      page = "homework-detail";
      break;
    case "team-homework":
    case "team-homework-detail":
      page = "supervisor-team-homework";
      break;
    case "caseload-engagement":
    case "homework-overview":
    case "completion-by-device":
    case "value-engagement":
      page = "reports";
      break;
    case "team-reports":
      page = "supervisor-team-reports";
      break;
    case "client-session-transcript":
    case "client-sessions":
    case "client-profile":
    case "client-edit":
    case "client-treatment":
    case "client-deferred":
      page = "client-profile";
      break;
    case "team-client-detail":
      page = "supervisor-team-client";
      break;
  }

  if ("clientSource" in route && route.clientSource === "team") {
    page = "supervisor-team-client";
  }

  return {
    page,
    ...(patientId ? { patientId } : {}),
    ...(patientId && patientNickname ? { patientNickname } : {}),
    ...(homeworkAssignmentId ? { homeworkAssignmentId } : {}),
  };
}

export function clinicianAssistantContextLabel(
  context: ClinicianAssistantContext,
): string {
  return context.patientNickname
    ? context.patientNickname
    : context.patientId
      ? "Selected client"
      : "";
}

export const teamClinicianAssistantStarters = [
  "Which clients across my team need attention?",
  "Are there recent safety events across my team?",
  "Who has not interacted with the assistant in seven days?",
  "Which team clients have no homework assigned?",
  "Which team clients have low homework completion?",
  "Which team clients are on track?",
  "Which therapists have clients needing attention?",
] as const;

export function clinicianAssistantStarters(
  context: ClinicianAssistantContext,
  scopeMode: "personal" | "team" = "personal",
): string[] {
  if (scopeMode === "team") return [...teamClinicianAssistantStarters];
  if (context.patientId) {
    return [
      "How is this client doing?",
      "What was their last completed homework?",
      "Does this client have overdue homework?",
      "Summarize their recent activity.",
    ];
  }
  return [
    "Which clients need my attention?",
    "Who has not used the assistant this week?",
    "Are there any recent safety events?",
  ];
}

export function pathForClinicianAssistantLink(
  link: ClinicianAssistantLink,
  currentRoute: ClinicianPortalRoute,
): string | null {
  const useTeamRoutes = currentRoute.globalKey === "team";

  switch (link.page) {
    case "client-profile":
      if (!link.patientId) return null;
      return useTeamRoutes
        ? teamClientDetailPath(link.patientId)
        : clientProfilePath(link.patientId);
    case "client-homework":
      if (!link.patientId) return null;
      return useTeamRoutes
        ? teamClientDetailPath(link.patientId)
        : clientPrimaryNavigation.find(
            (item) => item.key === "current-homework",
          )?.path(link.patientId) ?? null;
    case "client-insights":
      if (!link.patientId) return null;
      return useTeamRoutes
        ? teamClientDetailPath(link.patientId)
        : insightsNavigation.find((item) => item.key === "usage-engagement")
            ?.path(link.patientId) ?? null;
    case "homework-detail":
      if (!link.patientId || !link.homeworkAssignmentId) return null;
      return useTeamRoutes
        ? teamHomeworkDetailPath(link.patientId, link.homeworkAssignmentId)
        : clientHomeworkDetailPath(
            link.patientId,
            link.homeworkAssignmentId,
          );
    case "report": {
      if (!link.reportKey) return null;
      if (useTeamRoutes) {
        const reportKey = teamReportKeys[link.reportKey];
        return reportKey
          ? `${teamReportsPath}?report=${encodeURIComponent(reportKey)}`
          : null;
      }
      return reportPaths[link.reportKey] ?? null;
    }
    case "supervisor-team":
      return teamOverviewPath;
    case "supervisor-team-client":
      return link.patientId ? teamClientDetailPath(link.patientId) : null;
    case "supervisor-team-homework":
      if (link.patientId || link.homeworkAssignmentId) {
        return link.patientId && link.homeworkAssignmentId
          ? teamHomeworkDetailPath(link.patientId, link.homeworkAssignmentId)
          : null;
      }
      return teamHomeworkPath;
    case "supervisor-team-reports": {
      if (!link.reportKey) return teamReportsPath;
      const reportKey = teamReportKeys[link.reportKey];
      return reportKey
        ? `${teamReportsPath}?report=${encodeURIComponent(reportKey)}`
        : null;
    }
  }
}
